import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Edit,
  Eye,
  Trash2,
  Download,
  FileText,
  Calendar,
  DollarSign,
  Users,
  X,
  Upload,
  FileDown,
  List,
  FilePlus,
  Brain,
  MessageSquare,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Save
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import FormStepper from '../common/FormStepper';
import {
  contracts,
  contractTypes,
  paymentFrequencies,
  counterparties,
  assets,
} from '../../data/dummyData';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import ContractService from '../../services/contractService';
import api from '../../services/api';
import { useToast, ToastContainer } from '../common/Toast';

const ContractManagement = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast, loading: showLoading } = useToast();
  const [contractsList, setContractsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [generatingDocxId, setGeneratingDocxId] = useState(null);

  // Stepper state for contract creation
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const STEPS = [
    { id: 0, label: 'Contract Details', icon: FileText, hint: 'Basic contract information and parties' },
    { id: 1, label: 'Terms & Conditions', icon: FileText, hint: 'Contract terms, clauses, and conditions' },
    { id: 2, label: 'Financial Details', icon: DollarSign, hint: 'Pricing, payments, and financial terms' },
    { id: 3, label: 'Review & Submit', icon: Save, hint: 'Review all details and submit contract' }
  ];

  const handleStepClick = (stepIndex) => {
    if (stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  // Contract form state
  const [contractFormData, setContractFormData] = useState({
    title: '',
    contractId: '',
    type: '',
    counterparty: '',
    startDate: '',
    endDate: '',
    amount: '',
    currency: 'USD',
    description: '',
    terms: '',
    paymentTerms: '',
    deliverables: '',
    milestones: '',
    risks: '',
    status: 'draft'
  });

  const handleFormChange = (field, value) => {
    setContractFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleContractSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await ContractService.createContract(contractFormData);
      success('Contract created successfully!');
      setActiveTab('list');
      loadContracts();
      // Reset form
      setContractFormData({
        title: '',
        contractId: '',
        type: '',
        counterparty: '',
        startDate: '',
        endDate: '',
        amount: '',
        currency: 'USD',
        description: '',
        terms: '',
        paymentTerms: '',
        deliverables: '',
        milestones: '',
        risks: '',
        status: 'draft'
      });
      setCurrentStep(0);
    } catch (error) {
      console.error('Error creating contract:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create contract';
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contract Title *
                </label>
                <input
                  type="text"
                  value={contractFormData.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter contract title"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contract ID *
                </label>
                <input
                  type="text"
                  value={contractFormData.contractId}
                  onChange={(e) => handleFormChange('contractId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter contract ID"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contract Type *
                </label>
                <select
                  value={contractFormData.type}
                  onChange={(e) => handleFormChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select type</option>
                  <option value="service">Service Agreement</option>
                  <option value="supply">Supply Agreement</option>
                  <option value="license">License Agreement</option>
                  <option value="nda">NDA</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Counterparty *
                </label>
                <input
                  type="text"
                  value={contractFormData.counterparty}
                  onChange={(e) => handleFormChange('counterparty', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter counterparty name"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={contractFormData.startDate}
                  onChange={(e) => handleFormChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={contractFormData.endDate}
                  onChange={(e) => handleFormChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={contractFormData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter contract description"
              />
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Terms & Conditions *
              </label>
              <textarea
                value={contractFormData.terms}
                onChange={(e) => handleFormChange('terms', e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter contract terms and conditions"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Terms
              </label>
              <textarea
                value={contractFormData.paymentTerms}
                onChange={(e) => handleFormChange('paymentTerms', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter payment terms"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Deliverables
              </label>
              <textarea
                value={contractFormData.deliverables}
                onChange={(e) => handleFormChange('deliverables', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter deliverables"
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contract Value
                </label>
                <input
                  type="number"
                  value={contractFormData.amount}
                  onChange={(e) => handleFormChange('amount', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter contract value"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency
                </label>
                <select
                  value={contractFormData.currency}
                  onChange={(e) => handleFormChange('currency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="JPY">JPY</option>
                  <option value="CAD">CAD</option>
                  <option value="AUD">AUD</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Milestones
              </label>
              <textarea
                value={contractFormData.milestones}
                onChange={(e) => handleFormChange('milestones', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter contract milestones"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Risks & Mitigations
              </label>
              <textarea
                value={contractFormData.risks}
                onChange={(e) => handleFormChange('risks', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter risks and mitigation strategies"
              />
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Review Contract Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-gray-700">Title</p>
                  <p className="text-sm text-gray-900">{contractFormData.title || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Contract ID</p>
                  <p className="text-sm text-gray-900">{contractFormData.contractId || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Type</p>
                  <p className="text-sm text-gray-900">{contractFormData.type || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Counterparty</p>
                  <p className="text-sm text-gray-900">{contractFormData.counterparty || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Start Date</p>
                  <p className="text-sm text-gray-900">{contractFormData.startDate || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">End Date</p>
                  <p className="text-sm text-gray-900">{contractFormData.endDate || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Value</p>
                  <p className="text-sm text-gray-900">{contractFormData.amount ? `${contractFormData.currency} ${contractFormData.amount}` : 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Status</p>
                  <p className="text-sm text-gray-900">{contractFormData.status}</p>
                </div>
              </div>
              {contractFormData.description && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700">Description</p>
                  <p className="text-sm text-gray-900">{contractFormData.description}</p>
                </div>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Load contracts on component mount
  useEffect(() => {
    //console.log('=== ContractManagement useEffect START ===');
    //console.log('Component mounted, loading contracts...');
    //console.log('Current contracts state:', contractsList);
    //console.log('Current loading state:', loading);
    loadContracts();
    //console.log('=== ContractManagement useEffect END ===');
  }, []);

  // Load contracts from API
  const loadContracts = async (page = 1, searchTerm = '', filters = {}) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pagination.itemsPerPage,
        ...filters,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      //console.log('=== ContractManagement loadContracts START ===');
      //console.log('Loading contracts with params:', params);
      //console.log('Current contracts state before API call:', contractsList);

      const response = await ContractService.getContracts(params);

      //console.log('ContractService response:', response);
      //console.log('Response data:', response.data);
      //console.log('Response pagination:', response.pagination);
      //console.log('Response data type:', typeof response.data);
      //console.log('Response data length:', Array.isArray(response.data) ? response.data.length : 'Not an array');

      const raw = response.data || [];
      const newContracts = raw.map((c) => ({
        ...c,
        id: c.id || c._id || c.contractId,
      }));
      const newPagination = response.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
      };

      setContractsList(newContracts);
      setPagination(newPagination);

      //console.log('=== ContractManagement loadContracts END ===');
    } catch (error) {
      console.error('Error loading contracts:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
      showError(
        'Unable to load contracts. Please refresh the page or try again.'
      );
      setContractsList([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: pagination.itemsPerPage || 10,
      });
    } finally {
      setLoading(false);
    }
  };

  // Define columns for DataTable
  const columns = [
    {
      key: 'id',
      label: 'Contract ID',
      width: '150px',
      render: (value, row) => (
        <div className="d-flex align-items-center gap-2">
          <FileText size={16} style={{ color: 'var(--primary-color)' }} />
          <strong style={{ color: 'var(--text-primary)' }}>{value}</strong>
        </div>
      ),
    },
    {
      key: 'title',
      label: 'Title',
      width: '200px',
      render: (value, row) => (
        <div>
          <div className="fw-medium" style={{ color: 'var(--text-primary)' }}>
            {value}
          </div>
          <small style={{ color: 'var(--text-muted)' }}>v{row.version}</small>
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      width: '120px',
      type: 'badge',
      badgeClass: () => 'badge-info',
    },
    {
      key: 'counterparty',
      label: 'Counterparty',
      width: '180px',
      render: (value) => (
        <div className="d-flex align-items-center gap-2">
          <Users size={14} style={{ color: 'var(--text-muted)' }} />
          <span style={{ color: 'var(--text-primary)' }}>{value}</span>
        </div>
      ),
    },
    {
      key: 'startDate',
      label: 'Start Date',
      width: '120px',
      type: 'date',
      render: (value) => (
        <div className="d-flex align-items-center gap-2">
          <Calendar size={14} style={{ color: 'var(--text-muted)' }} />
          <span style={{ color: 'var(--text-primary)' }}>
            {new Date(value).toLocaleDateString()}
          </span>
        </div>
      ),
    },
    {
      key: 'endDate',
      label: 'End Date',
      width: '120px',
      type: 'date',
      render: (value, row) => {
        const daysUntilExpiry = getDaysUntilExpiry(value);
        return (
          <div className="d-flex align-items-center gap-2">
            <Calendar size={14} style={{ color: 'var(--text-muted)' }} />
            <span style={{ color: 'var(--text-primary)' }}>
              {new Date(value).toLocaleDateString()}
            </span>
            {daysUntilExpiry <= 30 && daysUntilExpiry > 0 && (
              <span
                className="badge"
                style={{
                  backgroundColor: 'var(--warning-color)',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: 'var(--border-radius-sm)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: '500',
                  marginLeft: 'var(--spacing-sm)',
                }}
              >
                {daysUntilExpiry}d left
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: '100px',
      type: 'badge',
      badgeClass: (value) => {
        const statusClasses = {
          Active: 'badge-success',
          Expired: 'badge-warning',
          Terminated: 'badge-danger',
          Draft: 'badge-secondary',
        };
        return statusClasses[value] || 'badge-secondary';
      },
    },
    // Amount column hidden to prevent horizontal scrollbar
    // {
    //   key: 'amount',
    //   label: 'Amount',
    //   width: '120px',
    //   type: 'currency',
    //   render: (value) => (
    //     <div className="d-flex align-items-center gap-2">
    //       <DollarSign size={14} style={{ color: 'var(--success-color)' }} />
    //       <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>${value.toLocaleString()}</span>
    //     </div>
    //   )
    // }
  ];

  // Helper function to get days until expiry
  const getDaysUntilExpiry = (endDate) => {
    const today = new Date();
    const expiry = new Date(endDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleEditContract = (contract) => {
    navigate(`/contracts/edit/${contract.id}`);
  };

  const handleViewContract = (contract) => {
    navigate(`/contracts/view/${contract.id}`);
  };

  const handleDeleteContract = (contract) => {
    setContractToDelete(contract);
    setShowDeleteModal(true);
  };

  // Confirm delete contract
  const confirmDeleteContract = async () => {
    if (!contractToDelete) return;

    setIsDeleting(true);

    try {
      //console.log('=== ContractManagement confirmDeleteContract START ===');
      //console.log('Deleting contract:', contractToDelete.id);

      const response = await ContractService.deleteContract(
        contractToDelete.id
      );

      //console.log('Delete response:', response);

      if (response.success) {
        success('Contract deleted successfully!');

        // Reload contracts list
        await loadContracts(pagination.currentPage);

        setShowDeleteModal(false);
        setContractToDelete(null);
      } else {
        showError(response.message || 'Failed to delete contract');
      }

      //console.log('=== ContractManagement confirmDeleteContract END ===');
    } catch (error) {
      console.error('Error deleting contract:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to delete contract';
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setContractToDelete(null);
    setIsDeleting(false);
  };

  const handleRenewContract = (contract) => {
    const newContract = {
      ...contract,
      id: `${contract.id}-RENEWED`,
      startDate: contract.endDate,
      endDate: new Date(
        new Date(contract.endDate).getTime() + 365 * 24 * 60 * 60 * 1000
      )
        .toISOString()
        .split('T')[0],
      version: contract.version + 1,
      status: 'Active',
    };
    setContractsList([...contractsList, newContract]);
  };

  const handleTerminateContract = (contract) => {
    setContractsList(
      contractsList.map((c) =>
        c.id === contract.id ? { ...c, status: 'Terminated' } : c
      )
    );
  };



  const handleCreateContract = () => {
    setActiveTab('add');
  };

  const handleGenerateDocx = async (contract) => {
    // Prefer Mongo _id (ObjectId); fall back to human-readable contractId (CT-XXXX)
    // Both are accepted by the backend endpoint.
    const docxId = contract._id || contract.id;
    if (!docxId) {
      showError('Cannot generate DOCX: contract ID is missing.');
      return;
    }

    setGeneratingDocxId(contract.id || contract._id);
    const toastId = showLoading('Generating DOCX, please wait...');

    try {
      const blobRes = await api.get(`/contracts/${docxId}/generate-docx`, {
        responseType: 'blob',
      });

      const blob = blobRes.data;
      const contentDisposition = blobRes.headers?.['content-disposition'] || '';
      const fileNameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
      const fileName = fileNameMatch
        ? fileNameMatch[1]
        : `Contract_${contract.id || docxId}.docx`;

      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objectUrl);

      removeToast(toastId);
      success('DOCX generated and downloaded successfully.');
    } catch (err) {
      removeToast(toastId);
      showError(
        err?.response?.data?.message ||
        err?.message ||
        'Failed to generate DOCX.',
      );
    } finally {
      setGeneratingDocxId(null);
    }
  };

  return (
    <div className=" ">
      <PageBreadcrumb />
      <PageHeader
        title="Contract Management"
        description="Manage lease agreements and contracts"
        actions={[
          {
            label: 'Create Contract',
            icon: <Plus size={18} />,
            onClick: handleCreateContract,
            variant: 'primary'
          }
        ]}
      />

      {/* Tabbed Navigation */}
      <div className="bg-white border border-slate-200 overflow-hidden ml-6 !mr-6 mb-8 mt-8">
        <div className="flex flex-wrap border-b border-slate-200 bg-white">
          <div className="flex-1 min-w-[140px]">
            <button
              onClick={() => setActiveTab('list')}
              className={`w-full flex items-center gap-2 px-5 py-4 justify-center bg-white
                transition-colors duration-200 border-b-2
                ${activeTab === 'list'
                  ? 'border-[var(--primary-color)]'
                  : 'border-transparent hover:bg-slate-50'
                }`
              }
            >
              <List
                size={18}
                className={
                  activeTab === 'list'
                    ? 'text-[var(--primary-color)]'
                    : 'text-slate-500'
                }
              />
              <span
                className={`text-sm whitespace-nowrap
            ${activeTab === 'list'
                    ? 'text-[var(--primary-color)] font-semibold'
                    : 'text-slate-500 font-medium'
                  }`}
              >
                Contract Registry
              </span>
            </button>
          </div>

          <div className="flex-1 min-w-[140px]">
            <button
              onClick={() => setActiveTab('add')}
              className={`w-full flex items-center justify-center gap-2 px-5 py-4 text-left bg-white
          transition-colors duration-200 border-b-2
          ${activeTab === 'add'
                  ? 'border-[var(--primary-color)]'
                  : 'border-transparent hover:bg-slate-50'
                }`}
            >
              <FilePlus
                size={18}
                className={
                  activeTab === 'add'
                    ? 'text-[var(--primary-color)]'
                    : 'text-slate-500'
                }
              />
              <span
                className={`text-sm whitespace-nowrap
            ${activeTab === 'add'
                    ? 'text-[var(--primary-color)] font-semibold'
                    : 'text-slate-500 font-medium'
                  }`}
              >
                Create Contract
              </span>
            </button>
          </div>

          <div className="flex-1 min-w-[140px]">
            <button
              onClick={() => setActiveTab('ai-assistant')}
              className={`w-full flex items-center justify-center gap-2 px-5 py-4 text-left bg-white
          transition-colors duration-200 border-b-2
          ${activeTab === 'ai-assistant'
                  ? 'border-[var(--primary-color)]'
                  : 'border-transparent hover:bg-slate-50'
                }`}
            >
              <MessageSquare
                size={18}
                className={
                  activeTab === 'ai-assistant'
                    ? 'text-[var(--primary-color)]'
                    : 'text-slate-500'
                }
              />
              <span
                className={`text-sm whitespace-nowrap
            ${activeTab === 'ai-assistant'
                    ? 'text-[var(--primary-color)] font-semibold'
                    : 'text-slate-500 font-medium'
                  }`}
              >
                AI Assistant
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="row ml-6 mr-6">
        <div className="col-12" style={{ overflowX: 'hidden' }}>
          {activeTab === 'list' && (
            <div className="page-tab-content">
              <div className="page-table-container">
                <DataTable
                  data={contractsList}
                  columns={columns}
                  title="Contract Registry"
                  loading={loading}
                  pagination={true}
                  pageSize={pagination.itemsPerPage || 10}
                  onPageChange={loadContracts}
                  searchable={true}
                  sortable={true}
                  exportable={true}
                  showCheckboxes={false}
                  onView={handleViewContract}
                  onEdit={handleEditContract}
                  onDelete={handleDeleteContract}
                  customActions={[
                    {
                      type: 'generate-docx',
                      label: generatingDocxId ? 'Generating...' : 'Generate DOCX',
                      disabled: generatingDocxId !== null,
                      onClick: handleGenerateDocx,
                    },
                  ]}
                  searchPlaceholder="Search contracts by ID, title, or counterparty..."
                  emptyMessage="No contracts found. Click 'Add Contract' to get started."
                />
              </div>
            </div>
          )}

          {activeTab === 'add' && (
            <div className="page-tab-content">
              <div
                className="card"
                style={{
                  borderRadius: '16px',
                  border: 'none',
                  boxShadow: '0 8px 32px rgba(0, 123, 143, 0.1)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                }}
              >

                <div className="card-body form-frame-content p-4">
                  <FormStepper
                    steps={STEPS}
                    currentStep={currentStep}
                    onStepClick={handleStepClick}
                  />
                  <div className="mt-6">
                    <form onSubmit={handleContractSubmit}>
                      {renderStepContent()}
                      <div className="flex justify-between mt-8">
                        <button
                          type="button"
                          onClick={handleBack}
                          disabled={currentStep === 0}
                          className="flex items-center gap-2 px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft size={16} />
                          Back
                        </button>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => setActiveTab('list')}
                            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                          >
                            Cancel
                          </button>
                          {currentStep < STEPS.length - 1 ? (
                            <button
                              type="button"
                              onClick={handleNext}
                              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              Next
                              <ChevronRight size={16} />
                            </button>
                          ) : (
                            <button
                              type="submit"
                              disabled={isSubmitting}
                              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {isSubmitting ? (
                                <>
                                  <Loader2 size={16} className="animate-spin" />
                                  Creating...
                                </>
                              ) : (
                                <>
                                  <Save size={16} />
                                  Create Contract
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ai-assistant' && (
            <div className="page-tab-content">
              <div
                className="card"
                style={{
                  backgroundColor: 'var(--white)',
                  borderRadius: 'var(--border-radius-lg)',
                  boxShadow: 'var(--shadow-md)',
                  border: '1px solid var(--border-color)',
                  padding: 'var(--spacing-xl)',
                  textAlign: 'center',
                }}
              >
                <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                  <MessageSquare
                    size={48}
                    style={{
                      color: 'var(--primary-color)',
                      marginBottom: 'var(--spacing-md)',
                    }}
                  />
                  <h4
                    style={{
                      color: 'var(--text-primary)',
                      marginBottom: 'var(--spacing-sm)',
                      fontFamily:
                        'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    }}
                  >
                    AI Contract Assistant
                  </h4>
                  <p
                    style={{
                      color: 'var(--text-secondary)',
                      fontFamily:
                        'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    }}
                  >
                    Get intelligent insights and assistance with your contracts
                  </p>
                </div>
                <div
                  style={{
                    padding: 'var(--spacing-lg)',
                    backgroundColor: 'var(--light-gray)',
                    borderRadius: 'var(--border-radius-md)',
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <p
                    style={{
                      color: 'var(--text-muted)',
                      margin: 0,
                      fontSize: 'var(--font-size-sm)',
                      fontFamily:
                        'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    }}
                  >
                    AI-powered contract analysis, clause suggestions, and
                    automated contract generation will be available soon. Stay
                    tuned for intelligent contract management features.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDeleteContract}
        title="Delete Contract"
        message={`Are you sure you want to delete "${contractToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default ContractManagement;
